import { parseCoordinatePair, validCoordinates } from "./locationValidation";
import { OpenLocationCode } from "open-location-code";
const plusCodes = new OpenLocationCode();
const HERE_GEOCODE_URL = "https://geocode.search.hereapi.com/v1/geocode";
const HERE_REVERSE_GEOCODE_URL = "https://revgeocode.search.hereapi.com/v1/revgeocode";
const HERE_AUTOSUGGEST_URL = "https://autosuggest.search.hereapi.com/v1/autosuggest";
const DEFAULT_AUTOSUGGEST_AT = { lat: 40.4168, lng: -3.7038 };

function encodeQueryValue(value) {
  return encodeURIComponent(String(value || "").trim());
}

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeHereItem(item) {
  const position = item?.position || {};
  const address = item?.address || {};
  const latitude = toFiniteNumber(position.lat);
  const longitude = toFiniteNumber(position.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return {
    lat: latitude,
    lng: longitude,
    address:
      item?.address?.label ||
      item?.title ||
      item?.vicinity ||
      "",
    name: item?.title || item?.address?.label || "",
    cityName: address.city || "",
    postalCode: address.postalCode || "",
    countryCode: String(address.countryCode || "").toUpperCase(),
  };
}

// Short Plus Codes need an explicit locality; never resolve against a default
// map centre, which could silently put the same code in another city.
function parsePlusCode(query) {
  const match = query.match(/^([23456789CFGHJMPQRVWX0]{2,8}\+[23456789CFGHJMPQRVWX]{2,7})(?:[\s,]+(.*))?$/i);
  if (!match) return null;
  const code = match[1].toUpperCase();
  return { code, locality: (match[2] || "").trim(), valid: plusCodes.isValid(code) };
}

async function resolvePlusCode(parsed, query, apiKey, language) {
  if (!parsed.valid) return null;
  let code = parsed.code;
  if (plusCodes.isShort(code)) {
    if (!parsed.locality) return null;
    const locality = await geocodeAddress(parsed.locality, apiKey, { language });
    if (!locality) return null;
    code = plusCodes.recoverNearest(code, locality.lat, locality.lng);
  }
  if (!plusCodes.isFull(code)) return null;
  const area = plusCodes.decode(code);
  const lat = area.latitudeCenter;
  const lng = area.longitudeCenter;
  const address = await reverseGeocode(lat, lng, apiKey, { language });
  // Reverse geocoding supplies the label, never replaces the decoded point.
  return { ...address, lat, lng, address: address?.address || query,
    name: address?.name || query, plusCode: code };
}

export async function geocodeAddress(address, apiKey, { language = "en", limit = 1 } = {}) {
  const query = String(address || "").trim();
  const normalizedKey = String(apiKey || "").trim();
  if (!query || !normalizedKey) return null;
  const plusCode = parsePlusCode(query);
  if (plusCode) return resolvePlusCode(plusCode, query, apiKey, language);
  const coordinates = parseCoordinatePair(query);
  if (coordinates) {
    if (coordinates.invalid) return null;
    const address = await reverseGeocode(coordinates.lat, coordinates.lng, apiKey, { language });
    return { ...address, ...coordinates, address: address?.address || query, lookupFailed: !address };
  }

  try {
    const response = await fetch(
      `${HERE_GEOCODE_URL}?q=${encodeQueryValue(query)}&limit=${limit}&lang=${encodeQueryValue(
        language
      )}&apiKey=${encodeQueryValue(normalizedKey)}`
    );
    if (!response.ok) return null;
    const data = await response.json();
    const item = Array.isArray(data?.items) ? data.items[0] : null;
    return normalizeHereItem(item);
  } catch (error) {
    console.error("HERE geocodeAddress failed:", error);
    return null;
  }
}

export async function reverseGeocode(lat, lng, apiKey, { language = "en" } = {}) {
  const latitude = toFiniteNumber(lat);
  const longitude = toFiniteNumber(lng);
  const normalizedKey = String(apiKey || "").trim();
  if (!validCoordinates(latitude, longitude) || !normalizedKey) return null;

  try {
    const response = await fetch(
      `${HERE_REVERSE_GEOCODE_URL}?at=${latitude.toFixed(7)},${longitude.toFixed(7)}&limit=1&lang=${encodeQueryValue(
        language
      )}&apiKey=${encodeQueryValue(normalizedKey)}`
    );
    if (!response.ok) return null;
    const data = await response.json();
    const item = Array.isArray(data?.items) ? data.items[0] : null;
    return normalizeHereItem(item);
  } catch (error) {
    console.error("HERE reverseGeocode failed:", error);
    return null;
  }
}

export async function autosuggestAddress(
  query,
  apiKey,
  { language = "en", limit = 6, at = null, throwOnError = false, addressesOnly = false } = {}
) {
  const normalizedQuery = String(query || "").trim();
  const normalizedKey = String(apiKey || "").trim();
  if (!normalizedQuery || !normalizedKey) return [];
  const plusCode = parsePlusCode(normalizedQuery);
  if (plusCode) {
    const result = await resolvePlusCode(plusCode, normalizedQuery, apiKey, language);
    return result ? [result] : [];
  }
  const coordinates = parseCoordinatePair(normalizedQuery);
  if (coordinates) {
    if (coordinates.invalid) return [];
    const address = await reverseGeocode(coordinates.lat, coordinates.lng, apiKey, { language });
    return [{ ...address, ...coordinates, address: address?.address || normalizedQuery, lookupFailed: !address }];
  }

  const requestedLatitude = toFiniteNumber(at?.lat);
  const requestedLongitude = toFiniteNumber(at?.lng);
  const latitude = Number.isFinite(requestedLatitude)
    ? requestedLatitude
    : DEFAULT_AUTOSUGGEST_AT.lat;
  const longitude = Number.isFinite(requestedLongitude)
    ? requestedLongitude
    : DEFAULT_AUTOSUGGEST_AT.lng;
  const atQuery = `&at=${encodeQueryValue(`${latitude.toFixed(7)},${longitude.toFixed(7)}`)}`;

  try {
    const response = await fetch(
      `${addressesOnly ? HERE_GEOCODE_URL : HERE_AUTOSUGGEST_URL}?q=${encodeQueryValue(normalizedQuery)}&limit=${encodeQueryValue(
        limit
      )}&lang=${encodeQueryValue(language)}${atQuery}&apiKey=${encodeQueryValue(normalizedKey)}`
    );
    if (!response.ok) {
      if (throwOnError) throw new Error(`Address search failed (${response.status})`);
      return [];
    }
    const data = await response.json();
    const items = Array.isArray(data?.items) ? data.items : [];
    return items.filter(item => !addressesOnly || item.resultType !== "place").map((item) => normalizeHereItem(item)).filter(Boolean);
  } catch (error) {
    if (throwOnError) throw error;
    console.error("HERE autosuggestAddress failed:", error);
    return [];
  }
}
